import { ReactElement, useEffect, useState } from 'react';
import { HeadTag } from 'shared/components';
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from '@mui/material';
import EmptyPlaceholder from 'shared/components/EmptyPlaceholder';
import { RiskAssessmentService } from 'services/RiskAssessmentService';
import DateFnsAdapter from '@date-io/date-fns';
import { Link } from 'react-router-dom';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import PageHeader from 'shared/components/PageHeader';
import RiskAssessmentsStatus from './components/RiskAssessmentsStatus';
import { Toast, Utils } from 'shared/helpers';
import DeleteIcon from '@mui/icons-material/Delete';
import { ConfirmDialog } from 'shared/components/AppComponents';
import { IAssessment } from '../risk-assessments/Main';
import CompletedAssessmentOverview from '../risk-assessments/components/CompletedAssessmentOverview';
import { BaseApplicationState } from 'state/baseapplication/Reducer';
import { useDispatch, useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';
import { PaginationActions } from 'state/pagination/Action';

const dateFns = new DateFnsAdapter();

const RiskAssessment = (): ReactElement => {
  const dispatch = useDispatch();
  const assessmentPagination = useSelector(
    (state: IAppState) => state.paginationState.assessment
  );

  const [isLoadingRecords, setIsLoadingRecords] = useState(false);
  const [assessments, setAssessments] = useState<IAssessment[]>([]);
  const [showAssementDetailFor, setShowAssementDetailFor] = useState('');
  const [type, setType] = useState('internal');
  const [statusCount, setStatusCount] = useState({
    total: 0,
    completed: 0,
    inProgress: 0,
    pending: 0,
  });
  const [deleteAssessmentId, setDeleteAssessmentId] = useState('');
  const { orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );

  const handlePageChange = (_event: unknown, page: number): void => {
    dispatch(
      PaginationActions.setPagination({
        assessment: {
          offset: page + 1,
        },
      })
    );
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const pageLimit = Number(event.target.value);

    dispatch(
      PaginationActions.setPagination({
        assessment: {
          offset: 1,
          pageLimit,
        },
      })
    );
  };

  // API Calls
  const loadRiskAssessments = (tType: string): void => {
    const queryString = new URLSearchParams({
      offset: String(assessmentPagination.offset),
      limit: String(assessmentPagination.pageLimit),
      enitityName: assessmentPagination.search,
      enitityType: assessmentPagination.enitityType,
      status: assessmentPagination.status,
      templateId: assessmentPagination.templateId,
      assessmentType: tType,
    }).toString();
    const promisess = [
      RiskAssessmentService.getExternalAssessments(queryString),
      RiskAssessmentService.getExternalAssessmentStats(),
    ];
    setIsLoadingRecords(true);
    Promise.all(promisess)
      .then((responses) => {
        const { data } = responses[0].data;
        const { assessment, pagination } = data;
        const { assessment: list } = assessment;
        setAssessments(list);
        const { data: stats } = responses[1].data;
        setStatusCount(stats);

        dispatch(
          PaginationActions.setPagination({
            assessment: {
              offset: pagination.offset,
              pageLimit: pagination.limit,
              totalRecords: pagination.total,
              totalPages: pagination.totalPages,
            },
          })
        );
      })
      .catch(() => {})
      .finally(() => {
        setIsLoadingRecords(false);
      });
  };

  // UI Actions
  const actionOnDeleteAssessment = (): void => {
    RiskAssessmentService.deleteExternalAssessment(deleteAssessmentId)
      .then(() => {
        Toast.showSuccessMessage('Assessment deleted successfully');
        setDeleteAssessmentId('');
        loadRiskAssessments(type);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  // Hooks
  useEffect(() => {
    loadRiskAssessments(type);
  }, [
    type,
    assessmentPagination.offset,
    assessmentPagination.pageLimit,
    assessmentPagination.search,
  ]);

  // UI Elements
  return (
    <>
      <PageHeader
        title="External Assessments"
        description="Track risk assessments sent to internal employees and external vendors."
      />

      <Box className="risk-management risk-assessment">
        <HeadTag title="Risk Assessments" />

        <Box className="risk-assessments-wrapper">
          <RiskAssessmentsStatus
            totalAssessments={statusCount.total}
            completedStatus={statusCount.completed}
            inProgress={statusCount.inProgress}
            pendingStatus={statusCount.pending}
          />

          <ButtonGroup className="app-group-button mt-5 mb-2">
            <Button
              className={type === 'internal' ? 'primary-light-fill' : ''}
              onClick={() => setType('internal')}
              variant={type === 'internal' ? 'contained' : 'text'}
            >
              Internal Assessments
            </Button>
            <Button
              className={type === 'external' ? 'primary-light-fill' : ''}
              onClick={() => setType('external')}
              variant={type === 'external' ? 'contained' : 'text'}
            >
              Vendor Assessments
            </Button>
          </ButtonGroup>

          {isLoadingRecords && (
            <Box className="empty-content">
              <CircularProgress />
            </Box>
          )}

          {!isLoadingRecords && (
            <TableContainer>
              <Table className="app-table" sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>AI Solution</TableCell>
                    {/* <TableCell>Template</TableCell> */}
                    <TableCell>Recipient</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Sent</TableCell>
                    <TableCell>Completed</TableCell>
                    <TableCell>Risk Level</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assessments.length > 0 ? (
                    assessments.map((row) => (
                      <TableRow
                        key={row._id}
                        sx={{
                          '& .MuiTableCell-root': { whiteSpace: 'nowrap' },
                          '&:last-child td, &:last-child th': {
                            border: 0,
                          },
                        }}
                      >
                        <TableCell
                          component="th"
                          scope="row"
                          className="word-break"
                        >
                          <Link
                            to={
                              row.entityType === 'AI System'
                                ? `/ai-systems/records/${row.enitityId}`
                                : `/datasets/records/${row.enitityId}`
                            }
                          >
                            {row.enitityName}
                          </Link>
                        </TableCell>
                        {/* <TableCell>{row.template.name}</TableCell> */}
                        <TableCell className="word-break">
                          <b>{row.toUserName}</b>
                          <br />
                          {row.toUserEmail}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.status}
                            size="small"
                            color={
                              row.status === 'Completed' ? 'primary' : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          {dateFns.date(row.assessmentDate).toDateString()}
                        </TableCell>
                        <TableCell>
                          {row.completionDate
                            ? dateFns.date(row.completionDate).toDateString()
                            : '-'}
                        </TableCell>
                        <TableCell>-</TableCell>
                        <TableCell>
                          <Box
                            style={{
                              display: 'flex',
                              columnGap: '10px',
                            }}
                          >
                            {((orgACLConfig.riskManagement?.includes(
                              'riskManagement.externalAssessments.internalAssessments.deleteAssessment'
                            ) &&
                              type === 'internal') ||
                              (orgACLConfig.riskManagement?.includes(
                                'riskManagement.externalAssessments.vendorAssessments.deleteAssessment'
                              ) &&
                                type === 'external')) && (
                              <Tooltip title="Delete">
                                <IconButton
                                  onClick={() => {
                                    setDeleteAssessmentId(row._id);
                                  }}
                                >
                                  <DeleteIcon color="error" />
                                </IconButton>
                              </Tooltip>
                            )}

                            {row.status === 'Completed' &&
                              ((orgACLConfig.riskManagement?.includes(
                                'riskManagement.externalAssessments.internalAssessments.viewAssessment'
                              ) &&
                                type === 'internal') ||
                                (orgACLConfig.riskManagement?.includes(
                                  'riskManagement.externalAssessments.vendorAssessments.viewAssessment'
                                ) &&
                                  type === 'external')) && (
                                <Tooltip title="View">
                                  <IconButton
                                    color="primary"
                                    onClick={() => {
                                      setShowAssementDetailFor(row._id);
                                    }}
                                  >
                                    <RemoveRedEyeIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <EmptyPlaceholder title="No assessments created yet" />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {!isLoadingRecords && assessments.length > 0 && (
            <TablePagination
              component="div"
              count={assessmentPagination.totalRecords}
              page={assessmentPagination.offset - 1}
              rowsPerPage={assessmentPagination.pageLimit}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 20, 50]}
              disabled={isLoadingRecords}
            />
          )}
        </Box>

        {showAssementDetailFor && (
          <CompletedAssessmentOverview
            open={!!showAssementDetailFor}
            assessmentId={showAssementDetailFor}
            onCloseDialog={() => {
              setShowAssementDetailFor('');
            }}
          />
        )}

        <ConfirmDialog
          title="Delete Assessment"
          subTitle="Are you sure to delete this assessment?"
          showConfirm={deleteAssessmentId}
          cancelTitle="Cancel"
          successTitle="Delete"
          onSuccess={actionOnDeleteAssessment}
          onCancel={() => setDeleteAssessmentId('')}
          onCloseDialog={() => setDeleteAssessmentId('')}
        />
      </Box>
    </>
  );
};

export default RiskAssessment;
